import { Check } from 'lucide-react';

import { cn } from '@/lib/cn';

interface BigOptionProps {
  active: boolean;
  onClick: () => void;
  label: string;
  hint?: string;
  /** El botón sube de alto en mobile para ser ultra-touchable. */
  size?: 'sm' | 'md';
}

/**
 * Botón grande estilo Airbnb. Padding generoso, bordes redondeados xl, label
 * grande y check en la esquina cuando está activo. Pensado para tocar con el
 * pulgar en una mano sin equivocarse.
 */
export function BigOption({ active, onClick, label, hint, size = 'md' }: BigOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'w-full text-left relative rounded-2xl border-2 transition-all active:scale-[0.98]',
        size === 'md' ? 'px-4 py-3.5 md:py-4' : 'px-3 py-2.5',
        active
          ? 'border-emerald-600 bg-emerald-50 text-notion-900'
          : 'border-notion-200 bg-white text-notion-700 hover:border-notion-400',
      )}
    >
      <div className={cn('font-medium', size === 'md' ? 'text-base' : 'text-sm')}>{label}</div>
      {hint && <div className="text-xs text-notion-500 mt-0.5">{hint}</div>}
      {active && (
        <Check
          size={16}
          className="absolute top-2 right-2 text-emerald-600"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

/**
 * Variante tipo "chip": redondeado completo, más compacto. Para listas
 * con muchas opciones (ej. aromas).
 */
export function BigChip({ active, onClick, label }: BigOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border-2 px-4 py-2 text-sm font-medium transition-all active:scale-95',
        active
          ? 'border-emerald-600 bg-emerald-600 text-white'
          : 'border-notion-200 bg-white text-notion-700 hover:border-notion-400',
      )}
    >
      {active && <Check size={14} aria-hidden="true" />}
      {label}
    </button>
  );
}
